import React, { useEffect } from 'react';
import { X, Mail, MessageCircle, Instagram, Facebook, Phone, MapPin, Navigation, ChevronRight } from 'lucide-react';
import { MAPS_URL, ukStatus } from '../lib/openingHours';

interface ContactModalProps {
  isOpen: boolean;
  onClose: () => void;
}

/** Contact routes as tappable rows. External links open in a new tab. */
const ROWS = [
  {
    title: 'Call us',
    icon: Phone,
    href: 'tel:01562227117',
    detail: '01562 227 117',
    note: 'Mon–Thu 10am–6pm  •  Fri 10am–4pm',
  },
  {
    title: 'WhatsApp',
    icon: MessageCircle,
    href: 'https://wa.me/441562227117',
    detail: 'Message us on WhatsApp',
    note: '01562 227 117',
  },
  {
    title: 'Email',
    icon: Mail,
    href: 'mailto:info@outpostcustom.co.uk',
    detail: 'info@outpostcustom.co.uk',
    note: null,
  },
] as const;

const SOCIALS = [
  { label: 'Instagram', icon: Instagram, href: 'https://instagram.com/outpostcustom' },
  { label: 'Facebook', icon: Facebook, href: 'https://facebook.com/outpostcustom' },
] as const;

const ContactModal: React.FC<ContactModalProps> = ({ isOpen, onClose }) => {
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const status = ukStatus();

  return (
    <div
      className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="contact-modal-title"
    >
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />

      {/* Same frame as ShowroomModal: bottom sheet on phones, two-column card from sm up */}
      <div className="relative bg-white w-full sm:max-w-[820px] max-h-[92svh] sm:max-h-[calc(100svh-2rem)] rounded-t-2xl sm:rounded-2xl shadow-2xl overflow-hidden flex flex-col sm:grid sm:grid-cols-[2fr_3fr]">
        <button
          autoFocus
          onClick={onClose}
          className="absolute top-3 right-3 z-20 p-2 rounded-full bg-black/40 text-white hover:bg-black/60 sm:bg-transparent sm:text-gray-500 sm:hover:bg-gray-100 transition-colors"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Photo panel */}
        <div className="relative shrink-0 h-40 sm:h-auto overflow-hidden bg-black">
          <img src="/home/team-2026.jpg" alt="" className="absolute inset-0 w-full h-full object-cover object-[center_35%]" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/35 to-black/10" />
          <div className="absolute inset-x-0 bottom-0 p-5 sm:p-7 text-white">
            <span
              className={`inline-flex items-center gap-2 rounded-full px-3 py-1 font-poppins text-[11px] font-semibold ${
                status.open ? 'bg-[#78BE20] text-white' : 'bg-white/15 text-white backdrop-blur'
              }`}
            >
              <span className={`h-1.5 w-1.5 rounded-full ${status.open ? 'bg-white animate-pulse' : 'bg-white/60'}`} />
              {status.label}
            </span>
            <h2
              id="contact-modal-title"
              className="font-montserrat font-extrabold tracking-[-0.03em] leading-[1.02] text-[28px] sm:text-[36px] mt-3"
            >
              Let&apos;s <span className="text-[#78BE20]">Chat</span>
            </h2>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 min-h-0 overflow-y-auto px-5 pt-5 pb-6 sm:px-7 sm:pt-8 sm:pb-7">
          <p className="font-poppins text-sm text-gray-600 leading-6 sm:pr-8">
            Get in touch with our team to chat about your project.
          </p>

          <ul className="mt-5 flex flex-col gap-2">
            {ROWS.map(({ title, icon: Icon, href, detail, note }) => {
              const external = href.startsWith('http');
              return (
                <li key={title}>
                  <a
                    href={href}
                    target={external ? '_blank' : undefined}
                    rel={external ? 'noopener noreferrer' : undefined}
                    className="group flex items-center gap-3.5 rounded-xl border border-gray-100 px-4 py-3 hover:border-[#78BE20] hover:bg-[#78BE20]/5 transition-colors"
                  >
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#78BE20]/10">
                      <Icon className="w-4 h-4 text-[#6da71d]" strokeWidth={2} />
                    </span>
                    <span className="min-w-0 flex-1 font-poppins">
                      <span className="block text-[10px] font-semibold tracking-[0.15em] uppercase text-gray-400">{title}</span>
                      <span className="block text-[13px] font-medium text-[#1a1a1a] break-words">{detail}</span>
                      {note && <span className="block text-xs text-gray-500 mt-0.5">{note}</span>}
                    </span>
                    <ChevronRight className="w-4 h-4 shrink-0 text-gray-300 transition-all group-hover:text-[#6da71d] group-hover:translate-x-0.5" />
                  </a>
                </li>
              );
            })}
          </ul>

          <a
            href={MAPS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="group mt-2 flex items-start gap-3.5 rounded-xl bg-gray-50 px-4 py-3 hover:bg-[#78BE20]/5 transition-colors"
          >
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white">
              <MapPin className="w-4 h-4 text-[#6da71d]" strokeWidth={2} />
            </span>
            <span className="min-w-0 flex-1 font-poppins">
              <span className="block text-[10px] font-semibold tracking-[0.15em] uppercase text-gray-400">Showroom</span>
              <span className="block text-[13px] font-medium text-[#1a1a1a] leading-5">
                23 (Unit 3) Lisle Avenue, Kidderminster, Worcestershire, DY11 7DF
              </span>
              <span className="block text-xs text-gray-400 mt-1">
                What3Words: <span className="font-medium text-gray-600">/// unity.rugs.firmly</span>
              </span>
            </span>
            <Navigation className="w-4 h-4 shrink-0 mt-2.5 text-gray-300 group-hover:text-[#6da71d] transition-colors" strokeWidth={2} />
          </a>

          <div className="mt-5 flex items-center gap-3 font-poppins">
            <span className="text-[10px] font-semibold tracking-[0.15em] uppercase text-gray-400">Follow us</span>
            {SOCIALS.map(({ label, icon: Icon, href }) => (
              <a
                key={label}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={label}
                className="flex h-9 w-9 items-center justify-center rounded-full border border-gray-200 text-gray-500 hover:border-[#78BE20] hover:text-[#6da71d] transition-colors"
              >
                <Icon className="w-4 h-4" strokeWidth={2} />
              </a>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactModal;
